import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Play, Pause, Wind, RotateCcw } from 'lucide-react';

export default function Breathing() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [exercises, setExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale');
  const [countdown, setCountdown] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);

  useEffect(() => {
    loadExercises();
  }, []);

  useEffect(() => {
    if (isActive && selectedExercise) {
      const phases = [
        { name: 'inhale', duration: selectedExercise.inhale },
        { name: 'hold', duration: selectedExercise.hold },
        { name: 'exhale', duration: selectedExercise.exhale }
      ];
      
      let currentPhaseIndex = phases.findIndex(p => p.name === phase);
      let timeLeft = countdown;

      const interval = setInterval(() => {
        timeLeft--;
        setCountdown(timeLeft);

        if (timeLeft <= 0) {
          currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
          const nextPhase = phases[currentPhaseIndex];
          setPhase(nextPhase.name);
          setCountdown(nextPhase.duration);
          timeLeft = nextPhase.duration;
          
          // Track cycles
          if (nextPhase.name === 'inhale') {
            setCycleCount(prev => prev + 1);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isActive, countdown, phase, selectedExercise]);

  const loadExercises = async () => {
    try {
      const response = await axios.get(`${API}/breathing-exercises`);
      setExercises(response.data);
      if (response.data.length > 0) {
        setSelectedExercise(response.data[0]);
        setCountdown(response.data[0].inhale);
      }
    } catch (error) {
      console.error('Failed to load exercises:', error);
    }
  };

  const handleStart = () => {
    if (!isActive) {
      setIsActive(true);
      setCycleCount(0);
      axios.post(
        `${API}/progress`,
        { activity_type: 'breathing', activity_id: selectedExercise.id },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(console.error);
    } else {
      setIsActive(false);
    }
  };

  const handleReset = () => {
    setIsActive(false);
    setPhase('inhale');
    setCountdown(selectedExercise?.inhale || 4);
    setCycleCount(0);
  };

  const handleExerciseSelect = (exercise) => {
    setIsActive(false);
    setSelectedExercise(exercise);
    setPhase('inhale');
    setCountdown(exercise.inhale);
    setCycleCount(0);
  };

  // Calculate dynamic scale based on phase and countdown
  const circleScale = useMemo(() => {
    if (!isActive || !selectedExercise) return 1;
    
    const totalDuration = phase === 'inhale' ? selectedExercise.inhale : 
                         phase === 'exhale' ? selectedExercise.exhale : 
                         selectedExercise.hold;
    const progress = 1 - (countdown / totalDuration);
    
    switch (phase) {
      case 'inhale':
        // Expand from 1 to 1.5 during inhale
        return 1 + (progress * 0.5);
      case 'hold':
        // Stay at 1.5 during hold
        return 1.5;
      case 'exhale':
        // Contract from 1.5 to 1 during exhale
        return 1.5 - (progress * 0.5);
      default:
        return 1;
    }
  }, [isActive, phase, countdown, selectedExercise]);

  const getPhaseColor = () => {
    switch (phase) {
      case 'inhale': return currentColors.accent;
      case 'hold': return currentColors.secondary;
      case 'exhale': return currentColors.primary;
      default: return currentColors.primary;
    }
  };

  const getPhaseInstruction = () => {
    switch (phase) {
      case 'inhale': return 'Breathe In';
      case 'hold': return 'Hold';
      case 'exhale': return 'Breathe Out';
      default: return 'Ready';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="breathing-page">
      {/* Header */}
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
            Sacred Breathing
          </h1>
          <p style={{ color: currentColors.textMuted }}>Align your breath with cosmic rhythms</p>
        </div>
      </div>

      {/* Exercise Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {exercises.map(exercise => (
          <button
            key={exercise.id}
            onClick={() => handleExerciseSelect(exercise)}
            className="glass-card p-6 text-left transition-all"
            style={{
              borderColor: selectedExercise?.id === exercise.id ? `${currentColors.primary}60` : 'transparent',
              boxShadow: selectedExercise?.id === exercise.id ? `0 0 20px ${currentColors.primary}20` : 'none'
            }}
            data-testid={`exercise-${exercise.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center space-x-2 mb-3">
              <Wind className="w-5 h-5" style={{ color: currentColors.accent }} />
              <h3 className="font-cinzel" style={{ color: currentColors.primary }}>{exercise.name}</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: currentColors.textMuted }}>{exercise.description}</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span 
                className="px-2 py-1 rounded"
                style={{ backgroundColor: `${currentColors.accent}20`, color: currentColors.accent }}
              >
                In: {exercise.inhale}s
              </span>
              <span 
                className="px-2 py-1 rounded"
                style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}
              >
                Hold: {exercise.hold}s
              </span>
              <span 
                className="px-2 py-1 rounded"
                style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
              >
                Out: {exercise.exhale}s
              </span>
            </div>
            <div className="mt-3">
              <span 
                className="text-xs px-2 py-1 rounded-full capitalize"
                style={{ 
                  backgroundColor: exercise.difficulty === 'beginner' ? 'rgba(34, 197, 94, 0.2)' :
                                   exercise.difficulty === 'intermediate' ? 'rgba(234, 179, 8, 0.2)' :
                                   'rgba(239, 68, 68, 0.2)',
                  color: exercise.difficulty === 'beginner' ? '#22c55e' :
                         exercise.difficulty === 'intermediate' ? '#eab308' :
                         '#ef4444'
                }}
              >
                {exercise.difficulty}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Breathing Animation */}
      {selectedExercise && (
        <div className="glass-card p-12 relative overflow-hidden">
          <div className="absolute inset-0 sacred-geometry opacity-10"></div>
          
          <div className="relative z-10">
            {/* Cycle Counter */}
            <div className="text-center mb-4">
              <span style={{ color: currentColors.textMuted }} className="text-sm">
                Cycles completed: <strong style={{ color: currentColors.primary }}>{cycleCount}</strong>
              </span>
            </div>

            {/* Breathing Circle Container */}
            <div className="relative w-80 h-80 mx-auto mb-8">
              {/* Outer decorative rings */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg className="w-72 h-72 animate-spin-slow opacity-30" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="48" fill="none" stroke={currentColors.primary} strokeWidth="0.3" strokeDasharray="2,4" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={currentColors.secondary} strokeWidth="0.3" strokeDasharray="3,5" />
                  <circle cx="50" cy="50" r="36" fill="none" stroke={currentColors.accent} strokeWidth="0.3" strokeDasharray="4,6" />
                </svg>
              </div>

              {/* Main breathing circle - ANIMATED */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="rounded-full transition-all duration-1000 ease-in-out"
                  style={{
                    width: '160px',
                    height: '160px',
                    transform: `scale(${circleScale})`,
                    background: `radial-gradient(circle at 30% 30%, ${getPhaseColor()}80, ${getPhaseColor()}40, ${getPhaseColor()}20)`,
                    boxShadow: isActive 
                      ? `0 0 ${30 + circleScale * 20}px ${getPhaseColor()}40, 
                         0 0 ${60 + circleScale * 30}px ${getPhaseColor()}20,
                         inset 0 0 30px ${getPhaseColor()}30`
                      : `0 0 20px ${currentColors.primary}20`,
                  }}
                  data-testid="breathing-circle"
                />
              </div>

              {/* Center content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                <div 
                  className="text-xl font-cinzel capitalize mb-2"
                  style={{ color: getPhaseColor() }}
                  data-testid="phase-text"
                >
                  {getPhaseInstruction()}
                </div>
                <div 
                  className="text-6xl font-cinzel"
                  style={{ color: currentColors.primary }}
                  data-testid="countdown-text"
                >
                  {countdown}
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4 mb-6">
              <button
                onClick={handleStart}
                className="cosmic-btn flex items-center space-x-3 px-8 py-4"
                style={{
                  backgroundColor: `${currentColors.primary}20`,
                  borderColor: currentColors.primary,
                  color: currentColors.primary
                }}
                data-testid="start-stop-button"
              >
                {isActive ? (
                  <><Pause className="w-5 h-5" /> <span>Pause</span></>
                ) : (
                  <><Play className="w-5 h-5" /> <span>Begin</span></>
                )}
              </button>
              
              <button
                onClick={handleReset}
                className="cosmic-btn flex items-center space-x-3 px-6 py-4"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: currentColors.textMuted,
                  color: currentColors.textMuted
                }}
                data-testid="reset-button"
              >
                <RotateCcw className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>

            {/* Phase indicators */}
            <div className="flex justify-center gap-8">
              {['inhale', 'hold', 'exhale'].map((p) => (
                <div 
                  key={p}
                  className="flex flex-col items-center transition-all"
                  style={{ 
                    opacity: phase === p ? 1 : 0.4,
                    transform: phase === p ? 'scale(1.1)' : 'scale(1)'
                  }}
                >
                  <div 
                    className="w-3 h-3 rounded-full mb-2"
                    style={{ 
                      backgroundColor: phase === p ? getPhaseColor() : currentColors.textMuted,
                      boxShadow: phase === p ? `0 0 10px ${getPhaseColor()}` : 'none'
                    }}
                  />
                  <span 
                    className="text-xs capitalize"
                    style={{ color: phase === p ? currentColors.text : currentColors.textMuted }}
                  >
                    {p}
                  </span>
                  <span className="text-xs" style={{ color: currentColors.textMuted }}>
                    {p === 'inhale' ? selectedExercise.inhale : 
                     p === 'hold' ? selectedExercise.hold : 
                     selectedExercise.exhale}s
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="glass-card p-6">
        <h3 className="font-cinzel mb-3" style={{ color: currentColors.primary }}>Breathing Tips</h3>
        <ul className="space-y-2 text-sm" style={{ color: currentColors.textMuted }}>
          <li>• Find a comfortable seated position with your spine straight</li>
          <li>• Breathe through your nose if possible</li>
          <li>• Let your belly expand naturally on the inhale</li>
          <li>• Release any tension in your shoulders and jaw</li>
        </ul>
      </div>
    </div>
  );
}
